import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  fetchCurrencies,
  createCurrency,
  updateCurrency,
  deleteCurrency,
} from "../../features/auth/store/currencySlice";
import { Currency } from "../../features/auth/store/types/currency-types";
import { CurrencyTable } from "./component";
import { ModalContainer } from "../../components/PageComponents";
import { OutlineButton, GradientButton } from "../../components/forms";

const CurrenciesManagementPage: React.FC = () => {
  const dispatch = useDispatch<any>();
  const items: Currency[] = useSelector(
    (state: any) => state.currencies?.items ?? [],
  );
  const loading: boolean = useSelector(
    (state: any) => state.currencies?.loading ?? false,
  );
  const saving: boolean = useSelector(
    (state: any) => state.currencies?.saving ?? false,
  );
  const error: string | null = useSelector(
    (state: any) => state.currencies?.error ?? null,
  );

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Currency | null>(null);
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [symbol, setSymbol] = useState("");
  const [exchangeRate, setExchangeRate] = useState<string>("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    dispatch(fetchCurrencies({ search }));
  }, [dispatch, search]);

  useEffect(() => {
    if (editing) {
      setName(editing.name);
      setCode(editing.code || "");
      setSymbol(editing.symbol);
      setExchangeRate(String(editing.exchange_rate ?? ""));
    } else {
      setName("");
      setCode("");
      setSymbol("");
      setExchangeRate("");
    }
  }, [editing, formOpen]);

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (item: Currency) => {
    setEditing(item);
    setFormOpen(true);
  };

  const handleDelete = (item: Currency) => {
    if (!window.confirm(`Delete currency ${item.name}?`)) return;
    dispatch(deleteCurrency(item.id));
  };

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    const payload = {
      name: name.trim(),
      code: code.trim(),
      symbol: symbol.trim(),
      exchange_rate: parseFloat(exchangeRate),
    };
    if (editing) {
      dispatch(updateCurrency({ id: editing.id, ...payload }));
    } else {
      dispatch(createCurrency(payload));
    }
    setFormOpen(false);
  };

  const sortedItems = useMemo(
    () =>
      [...items].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, {
          sensitivity: "base",
        }),
      ),
    [items],
  );

  return (
    <div className="p-6 dark:bg-zinc-950 min-h-screen transition-colors">
      <div className="flex items-center justify-between mb-4 gap-3">
        <div>
          <h2 className="text-xl font-semibold dark:text-white">Currencies</h2>
          <p className="text-xs text-gray-500 dark:text-zinc-400">
            Manage supported currencies, symbols, and exchange rates.
          </p>
        </div>
        <div className="flex gap-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search..."
            className="border rounded px-3 py-2 text-sm dark:bg-zinc-900 dark:border-zinc-700 dark:text-white dark:placeholder-zinc-500 outline-none focus:ring-1 focus:ring-blue-500"
          />
          <OutlineButton onClick={openCreate} content="New Currency" />
          <GradientButton
            onClick={() => dispatch(fetchCurrencies({ search }))}
            content="Refresh"
          />
        </div>
      </div>

      <div>
        <CurrencyTable
          items={sortedItems}
          loading={loading}
          onEdit={openEdit}
          onDelete={handleDelete}
        />
        {error && (
          <div className="text-sm text-red-600 dark:text-red-400 mt-2">
            {error}
          </div>
        )}
      </div>

      {formOpen && (
        <ModalContainer title={editing ? "Edit Currency" : "Create Currency"}>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs font-semibold mb-1 block dark:text-zinc-300">
                Name
              </label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full p-2 border rounded dark:bg-zinc-800 dark:border-zinc-700 dark:text-white outline-none focus:ring-1 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label className="text-xs font-semibold mb-1 block dark:text-zinc-300">
                Code
              </label>
              <input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="w-full p-2 border rounded dark:bg-zinc-800 dark:border-zinc-700 dark:text-white outline-none focus:ring-1 focus:ring-blue-500"
                maxLength={10}
                required
              />
            </div>
            <div>
              <label className="text-xs font-semibold mb-1 block dark:text-zinc-300">
                Symbol
              </label>
              <input
                value={symbol}
                onChange={(e) => setSymbol(e.target.value)}
                className="w-full p-2 border rounded dark:bg-zinc-800 dark:border-zinc-700 dark:text-white outline-none focus:ring-1 focus:ring-blue-500"
                maxLength={10}
                required
              />
            </div>
            <div>
              <label className="text-xs font-semibold mb-1 block dark:text-zinc-300">
                Exchange Rate
              </label>
              <input
                type="number"
                step="0.000001"
                min="0"
                value={exchangeRate}
                onChange={(e) => setExchangeRate(e.target.value)}
                className="w-full p-2 border rounded dark:bg-zinc-800 dark:border-zinc-700 dark:text-white outline-none focus:ring-1 focus:ring-blue-500"
                required
              />
            </div>

            <div className="flex gap-2 justify-end">
              <OutlineButton
                onClick={() => setFormOpen(false)}
                content="Cancel"
              />

              <GradientButton
                type="submit"
                disabled={saving}
                content={editing ? "Update" : "Create"}
              />
            </div>
          </form>
        </ModalContainer>
      )}
    </div>
  );
};

export default CurrenciesManagementPage;
